import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { useAuth } from "../lib/auth.js";
import { formatAuPhoneForDisplay } from "../lib/phone.js";
import { Badge } from "../lib/ui.js";
import { CallDetailModal } from "./CallDetail.js";
import { formatDateTime } from "../lib/datetime.js";
import { StickyTitle } from "../lib/StickyTitle.js";

interface CallListRow {
  id: string;
  caller_number: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  outcome: string | null;
  transfer_state: string | null;
  needs_review: boolean | null;
}

/**
 * Reads ONLY v_call_list -- never `calls`, and never call_raw/call_events (which RLS
 * wouldn't return to this role anyway -- see tests/integration/rls.test.ts). This is the
 * concrete enforcement of docs section 7: no AI/model internals reach the client UI.
 */
export function Calls() {
  const { activeBusinessId } = useAuth();
  const [rows, setRows] = useState<CallListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [openCallId, setOpenCallId] = useState<string | null>(null);

  useEffect(() => {
    if (!activeBusinessId) return;
    let cancelled = false;
    setLoading(true);
    supabase
      .from("v_call_list")
      .select("id, caller_number, started_at, ended_at, duration_seconds, outcome, transfer_state, needs_review")
      .eq("business_id", activeBusinessId)
      .order("started_at", { ascending: false })
      .limit(50)
      .then(({ data }) => {
        if (cancelled) return;
        setRows((data ?? []) as CallListRow[]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeBusinessId]);

  if (loading) return <p className="rc-loading">Loading...</p>;

  return (
    <div>
      <StickyTitle>
        <h1 className="rc-h1" style={{ margin: 0 }}>Calls</h1>
      </StickyTitle>
      <div className="rc-table-scroll rc-sticky-head"><table className="rc-stack" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
            <th style={{ padding: "10px 12px" }}>Caller</th>
            <th style={{ padding: "10px 12px" }}>Started</th>
            <th style={{ padding: "10px 12px" }}>Duration</th>
            <th style={{ padding: "10px 12px" }}>Outcome</th>
            <th style={{ padding: "10px 12px" }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
              <td data-label="Caller" style={{ padding: "10px 12px" }}>{row.caller_number ? formatAuPhoneForDisplay(row.caller_number) : "Unknown"}</td>
              <td data-label="Started" style={{ padding: "10px 12px" }}>{row.started_at ? formatDateTime(row.started_at) : "-"}</td>
              <td data-label="Duration" style={{ padding: "10px 12px" }}>{formatDuration(row.duration_seconds)}</td>
              <td data-label="Outcome" style={{ padding: "10px 12px" }}>
                {row.outcome ?? "-"}
                {row.needs_review && (
                  <span style={{ marginLeft: 8 }}>
                    <Badge tone="warning">Needs review</Badge>
                  </span>
                )}
              </td>
              <td className="rc-actions" style={{ padding: "10px 12px" }}>
                <button onClick={() => setOpenCallId(row.id)} style={{ background: "none", border: "none", color: "var(--rc-link)", cursor: "pointer", padding: 0, fontSize: 14 }}>
                  View
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} style={{ padding: 16, color: "var(--rc-text-faint)" }}>
                No calls yet.
              </td>
            </tr>
          )}
        </tbody>
      </table></div>
      {openCallId && <CallDetailModal callId={openCallId} onClose={() => setOpenCallId(null)} />}
    </div>
  );
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return "-";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}
